import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { Select, useAsyncEffect } from 'ui-toolkit';
import generateProgressBarProps from '../OrderProgress/generateProgressBarProps';
import titleize from '../../../../utilities/order/titleize';
import { convertAssociatedAddressApiToData, IAssociatedAddressesResponse } from './convertAssociatedAddressApiToData';
import { IAssociatedAddress } from './IAssociatedAddress';
import OrderDetailsItem from './OrderDetailsItem';
import { baseUrlService } from '../../../../ui-toolkit/rest/BaseUrlService';
import { APIFactory } from '../../../../ui-toolkit/rest/APIFactory';

const notSelf = (aa: IAssociatedAddress, id: string) => aa.id !== id;

const createStringIsInMatcher = (input?: string | null) => (source?: string | null) =>
  !!source && !!input && source.toLowerCase().includes(input.toLowerCase());

function isMatch(aa: IAssociatedAddress, input: string | null) {
  const inputIsIn = createStringIsInMatcher(input);
  return input
    ? inputIsIn(aa.address.street_address) || inputIsIn(aa.address.city) || inputIsIn(aa.fileNumber.toString())
    : true;
}

const getOptions = async (id: string) => {
  const result = await APIFactory(baseUrlService.ApiLocation).get<IAssociatedAddressesResponse>(
    `orders/v5/admin/${id}/associated_addresses`
  );
  return result.data.addresses.map((aa) => convertAssociatedAddressApiToData(aa));
};

const SelectWrapper = styled.div`
  .select .select__value-container {
    height: 50px;
  }
`;

const getOptionLabel: any = ({ value: associatedAddress }: { value: IAssociatedAddress }) => {
  const { items, position } = generateProgressBarProps({ ...associatedAddress });
  const progress = items[position];
  return (
    <OrderDetailsItem
      key={associatedAddress.id}
      address={associatedAddress.address}
      fileNumber={associatedAddress.fileNumber.toString()}
      transactionCategory={titleize(associatedAddress.transactionCategory)}
      progressHeading={progress.heading}
      progressSubheading={progress.subheading}
    />
  );
};

type AssociatedAddressOption = { label: string; value: IAssociatedAddress };

export interface IAssociatedOrdersSelectProps {
  id: string;
  onChange: (selectedOption: { label: ReactNode; value: IAssociatedAddress } | null) => void;
  streetAddress?: string | null;
  value?: string | null;
}

export const AssociatedOrdersSelect: React.FC<IAssociatedOrdersSelectProps> = ({
  id,
  onChange,
  streetAddress,
  value,
}) => {
  const [options, setOptions] = useState<AssociatedAddressOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  useAsyncEffect(async () => {
    const associatedAddresses = await getOptions(id);
    const loadedOptions = associatedAddresses
      .sort((a, b) => {
        const aMatches =
          !!a.address.street_address &&
          !!streetAddress &&
          !a.address.street_address.localeCompare(streetAddress, 'en', { sensitivity: 'base' });
        const bMatches =
          !!b.address.street_address &&
          !!streetAddress &&
          !b.address.street_address.localeCompare(streetAddress, 'en', { sensitivity: 'base' });
        // console.log('sorting', aMatches, bMatches, aMatches && !bMatches ? 1 : !aMatches && bMatches ? -1 : 0);
        return aMatches && !bMatches ? -1 : !aMatches && bMatches ? 1 : 0;
      })
      .map((aa) => ({ label: aa.fileNumber, value: aa }));
    // console.log({ associatedAddresses, loadedOptions, streetAddress });
    setOptions(loadedOptions);
    setIsLoading(false);
  }, [id]);
  const filterOption: any = ({ value: aa }: { value: IAssociatedAddress }, input: string | null) =>
    notSelf(aa, id) && (input ? isMatch(aa, input) : true);
  return (
    <SelectWrapper>
      <Select
        label="Property Address"
        filterOption={filterOption}
        getOptionLabel={getOptionLabel}
        isClearable={true}
        isLoading={isLoading}
        onChange={onChange}
        options={options}
        placeholder="Start typing to search"
        value={options.find((o) => (value ? o.value.id === value : false))}
      />
    </SelectWrapper>
  );
};
